import { CandidateList } from '../../app/feathers/candidate-lists/CandidateList';

export const CANDIDATE_LIST_STEPS = [
  { name: 'Upload Data', routeName: 'upload-data' },
  { name: 'Map Columns', routeName: 'map-columns' },
  { name: 'Verify Data', routeName: 'verify-data' },
  { name: 'Merge with Existing', routeName: 'merge-with-existing' },
  { name: 'Implementation districts', routeName: 'implementation-districts' },
  { name: 'Data Operations', routeName: 'data-operations' }
] as const;

export type CandidateListStepName = typeof CANDIDATE_LIST_STEPS[number]['routeName'];
export type CandidateListStatus = CandidateList['status'];
export type CandidateListRematchMode = 'all' | 'unmatched';

type MatchStats = {
  total: number;
  matched: number;
  new: number;
};

export const hasUnmatchedMatchingData = (
  raceStats: MatchStats,
  candidateStats: MatchStats
) => (
  raceStats.total > raceStats.matched + raceStats.new ||
  candidateStats.total > candidateStats.matched + candidateStats.new
);

export const getCandidateListRematchCommand = (mode: CandidateListRematchMode) => ({
  $rematchData: mode
});

export const isCandidateListStepName = (value?: string): value is CandidateListStepName => (
  CANDIDATE_LIST_STEPS.some(step => step.routeName === value)
);

export const getProgressStepIndex = (status?: CandidateListStatus, isNew = false) => {
  if (isNew) return 0;
  if (status === 'started') return 1;
  if (status === 'grouped') return 2;
  if (status === 'labeled') return 3;
  if (status === 'matched') return 4;
  if (status === 'scouted' || status === 'created') return 5;
  return 0;
};

export const isStepAvailable = (
  stepName: CandidateListStepName,
  status?: CandidateListStatus,
  isNew = false
) => {
  const stepIndex = CANDIDATE_LIST_STEPS.findIndex(step => step.routeName === stepName);
  const progressStepIndex = getProgressStepIndex(status, isNew);

  // Completed operations cannot be regenerated, so completed lists stay read-only
  // on the final view.
  if (status === 'created') return stepName === 'data-operations';
  if (stepIndex === progressStepIndex) return true;

  // Upload and mapping retain their original one-way progression. Once labeling
  // starts, completed review steps can be revisited without rolling status back.
  return stepIndex >= 2 && stepIndex <= progressStepIndex;
};

export const getDisplayedStepName = (
  activeStepName: string | undefined,
  status?: CandidateListStatus,
  isNew = false
): CandidateListStepName => {
  if (
    isCandidateListStepName(activeStepName) &&
    isStepAvailable(activeStepName, status, isNew)
  ) {
    return activeStepName;
  }

  return CANDIDATE_LIST_STEPS[getProgressStepIndex(status, isNew)].routeName;
};

export const getDisplayedStatus = (
  stepName: CandidateListStepName,
  actualStatus?: CandidateListStatus
): CandidateListStatus | undefined => {
  if (stepName === 'map-columns') return 'started';
  if (stepName === 'verify-data') return 'grouped';
  if (stepName === 'merge-with-existing') return 'labeled';
  if (stepName === 'implementation-districts') return 'matched';
  if (stepName === 'data-operations') {
    return actualStatus === 'created' ? 'created' : 'scouted';
  }
  return undefined;
};

export const markRaceMatchAsUser = (
  raceMatch: { race: string; createAsNew: boolean } | null
) => raceMatch ? { ...raceMatch, labeledBy: 'user' as const } : null;
